import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { Separator } from "@components/ui/separator";
import Error from "@components/ui/error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChevronsUpDownIcon, SearchIcon } from "lucide-react";
import {
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import {
  PreviewFormSubmitButton,
  SearchPreviewFormType,
  useSearchPreviewFormSchema,
} from "./types";
import Query from "./Query";
import { Fragment, useMemo } from "react";
import { useSearchContext } from "../context";
import { capitalize, formatDateForApi } from "@/utils";
import { parseISO } from "date-fns";
import MentionCardNew from "@components/MentionCardNew";
import { MentionCardsProvider } from "@/containers/MentionCardsContext";
import {
  generatePath,
  Redirect,
  useHistory,
  useParams,
} from "react-router-dom";
import { Badge } from "@components/ui/badge";
import { useI18n } from "@/contexts/i18nContext";
import { Spinner } from "@components/ui/spinner";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@components/ui/empty";
import { ROUTES } from "@/routes_new";
import PaginationNew from "@components/PaginationNew";
import { AnimatePresence, motion } from "motion/react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@components/ui/collapsible";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import Staff from "@/containers/QueryBuilderNew/partials/Staff";
import { toast } from "sonner";
import { UnsavedQuery } from "@/types/SearchQuery";
import copyToClipboard from "@/utils/copyToClipboard";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";

const PAGE_SIZE = 20;
const TOTAL_BADGE_CAP = 1000;

export default function PreviewForm() {
  const { t } = useLingui();
  const { formatNumber } = useI18n();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();
  const history = useHistory();

  // Context
  const {
    tags,
    languages,
    sources,
    audioSources,
    newsSources,
    socialSources,
    minReachByChannelTypeSources,
    candidateSearch,
    setCandidateSearch,
    previewData,
    isLoading,
    previewError,
    page,
    setPage,
  } = useSearchContext();

  // Helpers
  const previewFormSchema = useSearchPreviewFormSchema(
    minReachByChannelTypeSources
  );
  const resolver = useMemo(
    () => zodResolver(previewFormSchema),
    [previewFormSchema]
  );

  // Form state
  const form = useForm<SearchPreviewFormType>({
    resolver,
    defaultValues: {
      information_need: candidateSearch?.information_need ?? null,
      start_date: candidateSearch?.start_date
        ? parseISO(candidateSearch.start_date)
        : undefined,
      end_date: candidateSearch?.end_date
        ? parseISO(candidateSearch.end_date)
        : null,
      queries: candidateSearch?.queries,
    },
  });

  const queriesField = useFieldArray({
    control: form.control,
    name: "queries",
  });

  const queries = useWatch({
    control: form.control,
    name: "queries",
  });

  // Handlers
  function handleApplySearchTemplate(queries: UnsavedQuery[]) {
    queriesField.replace(queries);
  }

  async function handleCopyQueries() {
    try {
      const jsonString = JSON.stringify({ queries }, null, 2);
      await copyToClipboard(jsonString);
      toast.success(t`JSON kopierades till urklipp`);
    } catch {
      toast.error(t`Kunde inte kopiera JSON`);
    }
  }

  const handleSubmit: SubmitHandler<SearchPreviewFormType> = function (
    values,
    e
  ) {
    setCandidateSearch({
      title: values.queries[0].keywords[0]
        ? capitalize(values.queries[0].keywords[0])
        : undefined,
      end_date: values.end_date ? formatDateForApi(values.end_date) : undefined,
      information_need: values.information_need || undefined,
      start_date: formatDateForApi(values.start_date),
      queries: values.queries,
    });

    setPage(1);

    form.reset(values, { keepValues: true });

    const nativeEvent = e?.nativeEvent as SubmitEvent;
    const submitter = nativeEvent?.submitter as HTMLButtonElement;
    const submitterName = submitter?.name as PreviewFormSubmitButton;

    if (submitterName === PreviewFormSubmitButton.Create) {
      setTimeout(() => {
        history.push(
          generatePath(ROUTES.SEARCH_SUBMIT_ORGID, {
            orgUuid,
          })
        );
      }, 0);
    }
  };

  const mentionCards = useMemo(() => {
    if (!previewData) return [];
    return previewData.results;
  }, [previewData]);
  const totalMatches = previewData?.count ?? 0;

  if (!candidateSearch) {
    return <Redirect to={generatePath(ROUTES.SEARCH_ORGID, { orgUuid })} />;
  }

  return (
    <>
      <AccessControl level={ACCESS_LEVELS.STAFF}>
        <Staff<UnsavedQuery>
          onApplySearchQueries={handleApplySearchTemplate}
          onCopyQueries={handleCopyQueries}
          className="mb-4"
        />
      </AccessControl>

      <form id="search-preview-form" onSubmit={form.handleSubmit(handleSubmit)}>
        <Collapsible
          asChild
          defaultOpen={false}
          onOpenChange={(value) => {
            if (value) {
              postTrackingData(EVENT.SEARCH_SETTINGS_OPENED);
            } else {
              postTrackingData(EVENT.SEARCH_SETTINGS_CLOSED);
            }
          }}
        >
          <Card className="data-[state=closed]:p-0 data-[state=open]:pt-0 group/card">
            <CollapsibleTrigger asChild>
              <button
                className="text-left w-full"
                id="form-qb-toggle-settings"
                data-testid="form-qb-toggle-settings"
              >
                <CardHeader className="flex items-start justify-between group/card-header group-data-[state=open]/card:pt-6 group-data-[state=closed]/card:p-6">
                  <div className="flex flex-col gap-2">
                    <CardTitle>
                      <Trans>Sökord & inställningar</Trans>
                    </CardTitle>
                    <CardDescription className="group-data-[state=closed]/card:sr-only">
                      <Trans>
                        Få mer relevanta resultat genom att förfina din sökning
                        med sambandsord & exkludera ord.
                      </Trans>
                    </CardDescription>
                  </div>

                  <ChevronsUpDownIcon className="size-4 text-muted-foreground group-hover/card-header:text-foreground transition" />
                </CardHeader>
              </button>
            </CollapsibleTrigger>

            <CollapsibleContent asChild>
              <CardContent>
                {queriesField.fields.map((query, queryIndex) => {
                  return (
                    <Fragment key={query.id}>
                      {queryIndex ? <Separator /> : null}

                      <Query
                        minReachByChannelTypeSources={
                          minReachByChannelTypeSources
                        }
                        socialSources={socialSources}
                        queriesField={queriesField}
                        audioSources={audioSources}
                        newsSources={newsSources}
                        languages={languages}
                        index={queryIndex}
                        queries={queries}
                        sources={sources}
                        form={form}
                        tags={tags}
                      />
                    </Fragment>
                  );
                })}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </form>

      <div className="flex flex-col pt-8">
        <div className="flex flex-col gap-1.5 px-6">
          <div className="flex items-center gap-2.5">
            <h2 className="text-xl font-semibold">
              <Trans>Sökresultat</Trans>
            </h2>
            <Badge variant="secondary" data-testid="estimated-mention-count">
              {totalMatches > TOTAL_BADGE_CAP
                ? `${formatNumber(TOTAL_BADGE_CAP)}+`
                : formatNumber(totalMatches)}
            </Badge>
          </div>

          <p className="text-sm text-muted-foreground">
            <Trans>
              Förhandsvisar resultat, följ för att få hela bilden över tid.
            </Trans>
          </p>
        </div>

        <AnimatePresence mode="popLayout">
          {previewData ? (
            mentionCards.length ? (
              <MentionCardsProvider>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col gap-8 mt-7"
                  key="mentions"
                >
                  {mentionCards.map((card) => (
                    <MentionCardNew
                      key={card.episode.id}
                      mentionCard={card}
                      orgUuid={orgUuid}
                      tags={tags}
                      readOnly
                    />
                  ))}

                  <PaginationNew
                    page={page}
                    max={Math.ceil(totalMatches / PAGE_SIZE)}
                    onChangePage={setPage}
                  />
                </motion.div>
              </MentionCardsProvider>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key="empty"
              >
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <SearchIcon />
                    </EmptyMedia>
                    <EmptyTitle>
                      <Trans>Inga resultat</Trans>
                    </EmptyTitle>
                    <EmptyDescription>
                      <Trans>
                        Vi kunde tyvärr inte hitta några sökresultat för den här
                        sökningarn. Vänligen justera dina söktermer eller
                        filter, och försök igen.
                      </Trans>
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              </motion.div>
            )
          ) : isLoading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              key="loading"
            >
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Spinner />
                  </EmptyMedia>
                  <EmptyTitle>
                    <Trans>Söker</Trans>
                  </EmptyTitle>
                  <EmptyDescription>
                    <Trans>
                      Vi söker just nu efter träffar på din sökning. Ge oss
                      några sekunder bara.
                    </Trans>
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </motion.div>
          ) : (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Error
                heading={t`Åh nej!`}
                description={t`Något gick snett med att ladda sökresultaten. Vänligen försök igen senare`}
                error={previewError}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
